import React from 'react';
import Modal from '@/shared/components/Modal';
import { BodyText, H2 } from '@/shared/components/StyledText';
import styled, { css } from '@emotion/native';
import Button from '@/shared/components/Button';
import { sand, white, green } from '@/shared/styles/colors';
import ModalWrapper from '@/shared/styles/components/ModalWrapper';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { Pressable, Image } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Props {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  onEndSessionAndExit: () => void;
  onExitCallOnly: () => void;
  isStylist: boolean;
}

const EndCallModal = ({
  isVisible,
  setIsVisible,
  onEndSessionAndExit,
  onExitCallOnly,
  isStylist,
}: Props) => {
  const { isDesktop } = useBreakpoint();
  const insets = useSafeAreaInsets();
  const onDismiss = () => {
    setIsVisible(false);
  };

  const handleEndSessionAndExit = () => {
    onEndSessionAndExit();
  };

  const handleExitCallOnly = () => {
    onExitCallOnly();
  };

  const userType = isStylist ? 'client' : 'stylist';

  return (
    <Modal
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      onDismiss={onDismiss}
      onRequestClose={onDismiss}
    >
      <ModalWrapper
        style={{ ...containerStyle, maxHeight: isDesktop ? 800 : 'auto' }}
      >
        <CloseButton
          onPress={onDismiss}
          isDesktop={isDesktop}
          topInset={insets.top}
        >
          <CloseIcon source={require('@/assets/images/CloseGreen.png')} />
        </CloseButton>
        <Title>Choose Your Exit Option</Title>
        <SubTitle>How would you like to end this session?</SubTitle>

        <ButtonContainer>
          <Button
            onPress={handleEndSessionAndExit}
            text="End Session & Exit"
            variant="primary"
            customStyle={endSessionButtonStyle}
          />
          <ExplanationText>
            This will permanently end the session for both you and your{' '}
            {userType}. The session cannot be rejoined.
          </ExplanationText>

          <Button
            onPress={handleExitCallOnly}
            text="Exit Call Only"
            variant="secondary"
            customStyle={exitCallButtonStyle}
          />
          <ExplanationText>
            You&apos;ll leave the call but keep the session active. You and your{' '}
            {userType} can rejoin for a limited time.
          </ExplanationText>
        </ButtonContainer>
      </ModalWrapper>
    </Modal>
  );
};

export default EndCallModal;

const containerStyle = css({
  flex: 1,
  width: '100%',
  maxWidth: 697,
  backgroundColor: sand,
  borderRadius: 12,
  alignItems: 'center',
  justifyContent: 'center',
  paddingHorizontal: 28,
  paddingVertical: 40,
});

const Title = styled(H2)({
  marginBottom: 12,
  textTransform: 'uppercase',
  textAlign: 'center',
  color: green,
});

const SubTitle = styled(BodyText)({
  marginBottom: 36,
  fontSize: 16,
  lineHeight: 20,
  textAlign: 'center',
  color: green,
});

const ButtonContainer = styled.View({
  width: '100%',
  alignItems: 'center',
});

const ExplanationText = styled(BodyText)({
  width: '90%',
  marginBottom: 24,
  fontSize: 14,
  lineHeight: 18,
  textAlign: 'center',
  color: green,
  opacity: 0.8,
});

const endSessionButtonStyle = css({
  backgroundColor: green,
  marginBottom: 12,
  width: '100%',
});

const exitCallButtonStyle = css({
  backgroundColor: white,
  borderWidth: 2,
  borderColor: green,
  marginBottom: 12,
  width: '100%',
});

interface CloseButtonProps {
  isDesktop: boolean;
  topInset: number;
}

const CloseButton = styled(Pressable)<CloseButtonProps>(
  ({ isDesktop, topInset }) => ({
    position: 'absolute',
    top: isDesktop ? 22 : 30 + topInset,
    right: isDesktop ? 24 : 32,
    zIndex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  }),
);

const CloseIcon = styled(Image)({
  width: 24,
  height: 24,
  resizeMode: 'contain',
});
